/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '@modules/components/auth/auth.service';
import AuthApiService from '@app/shared/services/auth/auth-api.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { isOnPublicRoute } from '../utils/public-route.util';

@Injectable({
  providedIn: 'root',
})
export class RefreshTokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private accessTokenSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);

  constructor(
    private authService: AuthService,
    private authApiService: AuthApiService,
    private router: Router,
    private modalService: NzModalService,
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (
          error.status === 401 &&
          !request.url.includes('/auth/login') &&
          !request.url.includes('/auth/refresh-tokens')
        ) {
          return this.handle401Error(request, next);
        }

        return throwError(error);
      }),
    );
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.accessTokenSubject.next(null);

      return this.authApiService.refreshToken().pipe(
        switchMap(({ access_token, refresh_token, user }) => {
          this.isRefreshing = false;
          this.authService.setLoggedInData(access_token, refresh_token, user);
          this.accessTokenSubject.next(access_token);

          const clonedRequest = request.clone({
            setHeaders: {
              Authorization: 'Bearer ' + access_token,
            },
          });

          return next.handle(clonedRequest);
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.authService.removeToken();

          if (!isOnPublicRoute(this.router)) {
            this.router.navigate(['/login']);
            this.modalService.closeAll();
          }

          return throwError(() => err);
        }),
      );
    } else {
      return this.accessTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          const clonedRequest = request.clone({
            setHeaders: {
              Authorization: 'Bearer ' + token,
            },
          });

          return next.handle(clonedRequest);
        }),
      );
    }
  }
}
